import { apiEndpoints } from '@/apiEndpoints';
import {
  Circuit,
  DistributedCircuit,
  DistributorCircuit,
} from '@/types/circuits/circuitColumnDef';
import { CircuitResponse } from '@/types/circuits/CircuitResponse';
import { EditableCircuit } from '@/types/circuits/EditableCircuitsColumnDef';
import { Draft } from '@/types/dashboard/draftColumnDef';
import { SupportedLocale } from '@/types/supported-locale.type';
import axios from 'axios';

interface CircuitsResponse {
  total: number;
  circuits: Circuit[];
}

export interface CircuitParams {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  filters?: Record<string, string>;
}

export const getCircuits = async ({
  page = 1,
  pageSize = 10,
  sortField = '',
  sortOrder = 'asc',
  filters = {},
}: CircuitParams) => {
  const filtersStr = Object.entries(filters)
    .map(([key, value]) => `${key}:${encodeURIComponent(value)}`)
    .join(',');
  const result = await axios.get<CircuitsResponse>(apiEndpoints.circuits.base, {
    params: {
      page,
      pageSize,
      sortField,
      sortOrder,
      filters: filtersStr,
    },
  });
  return result.data;
};

export type DistributorCircuitsResponse = {
  circuits: DistributedCircuit[];
  total: number;
  countInvalidatedCircuits: number;
  countRefusedCircuits: number;
  countEditableCircuits: number;
};

export type DistributorCircuitsParams = CircuitParams & {
  shouldFetchBenefits?: boolean;
};

export const getDistributorCircuits = async ({
  page = 1,
  pageSize = 10,
  sortField = '',
  sortOrder = 'asc',
  filters = {},
  shouldFetchBenefits = false,
}: DistributorCircuitsParams) => {
  const filtersStr = Object.entries(filters)
    .map(([key, value]) => `${key}:${encodeURIComponent(value)}`)
    .join(',');
  const result = await axios.get<DistributorCircuitsResponse>(
    apiEndpoints.circuits.distributors,
    {
      params: {
        page,
        pageSize,
        sortField,
        sortOrder,
        filters: filtersStr,
        shouldFetchBenefits,
      },
    },
  );
  return result.data;
};

type UndistributedCircuitsResponse = {
  circuits: DistributorCircuit[];
  total: number;
};

export type InvalidatedCircuitsParams = CircuitParams & {
  onlyRefused?: boolean;
};

export const getUndistributedCircuits = async ({
  page = 1,
  pageSize = 10,
  sortField = '',
  sortOrder = 'asc',
  filters = {},
  onlyRefused = false,
}: InvalidatedCircuitsParams) => {
  const filtersStr = Object.entries(filters)
    .map(([key, value]) => `${key}:${encodeURIComponent(value)}`)
    .join(',');
  const result = await axios.get<UndistributedCircuitsResponse>(
    apiEndpoints.circuits.distributorsInvalidated,
    {
      params: {
        page,
        pageSize,
        sortField,
        sortOrder,
        filters: filtersStr,
        onlyRefused,
      },
    },
  );
  return result.data;
};

export type CountriesWithCircuits = Array<{ country: string }>;

export const getCountriesWithCircuits = async (onlyPublished = false) => {
  const result = await axios.get<CountriesWithCircuits>(
    apiEndpoints.circuits.countries,
    { params: { onlyPublished } },
  );
  return result.data;
};

export type DurationsWithCircuits = Array<{
  durationDays: number;
  durationNights: number;
}>;

export const getDurationsWithCircuits = async () => {
  const result = await axios.get<DurationsWithCircuits>(
    apiEndpoints.circuits.durations,
  );
  return result.data;
};

export const deleteCircuit = async (circuitId: string) => {
  const result = await axios.delete(apiEndpoints.circuits.detail(circuitId));
  return result.data;
};

export const deleteMultipleCircuits = async (circuitIds: string[]) => {
  const result = await axios.delete(apiEndpoints.circuits.multiple, {
    data: { circuitIds },
  });
  return result.data;
};

export const createCircuit = async (circuit: FormData) => {
  const result = await axios.post(apiEndpoints.circuits.base, circuit);
  return result.data;
};

export const updateCircuit = async (
  circuitId: string,
  circuit: FormData,
  language?: SupportedLocale,
) => {
  const result = await axios.put(
    apiEndpoints.circuits.detail(circuitId),
    circuit,
    { params: { language } },
  );
  return result.data;
};

export const getDraftsCircuits = async () => {
  const result = await axios.get<Draft[]>(apiEndpoints.circuits.drafts);
  return result.data;
};

export type GetCircuitOptions = {
  signImages?: 'all' | 'mainOnly';
  skipTranslations?: boolean;
  pricingPreview?: boolean;
};

export const getCircuit = async (
  circuitId: string,
  originalImage = false,
  shouldConvertToEuro = false,
  options: GetCircuitOptions = {},
) => {
  const result = await axios.get<CircuitResponse>(
    apiEndpoints.circuits.detail(circuitId),
    {
      params: {
        originalImage,
        shouldConvertToEuro,
        signImages: options.signImages ?? 'all',
        skipTranslations: options.skipTranslations ?? false,
        pricingPreview: options.pricingPreview ?? false,
      },
    },
  );
  return result.data;
};

export const duplicateCircuit = async (circuitId: string, fields: string[]) => {
  const result = await axios.post(apiEndpoints.circuits.duplicate(circuitId), {
    fields,
  });
  return result.data;
};

export const acceptCircuitToDistribute = async (circuitIds: string[]) => {
  const result = await axios.post(apiEndpoints.circuits.distribute, {
    circuitIds,
  });
  return result.data;
};

export const withdrawCircuit = async (circuitId: string) => {
  const result = await axios.post(apiEndpoints.circuits.withdraw(circuitId));
  return result.data;
};

export type ReceiversWithCircuits = Array<{
  id: string;
  name: string;
}>;

export const findDistributorCircuitReceivers = async (
  isDistributed: boolean,
) => {
  const result = await axios.get<ReceiversWithCircuits>(
    apiEndpoints.circuits.distributorsReceivers,
    { params: { isDistributed } },
  );
  return result.data;
};

export const findDistributorCircuitCountries = async (
  isDistributed: boolean,
) => {
  const result = await axios.get<CountriesWithCircuits>(
    apiEndpoints.circuits.distributorsCountries,
    { params: { isDistributed } },
  );
  return result.data;
};

export const findDistributorCircuitDurations = async (
  isDistributed: boolean,
) => {
  const result = await axios.get<DurationsWithCircuits>(
    apiEndpoints.circuits.distributorsDurations,
    { params: { isDistributed } },
  );
  return result.data;
};

export const getOriginalCircuitImages = async (
  circuitId: string,
  accessToken?: string,
) => {
  const result = await axios.get<string[]>(
    apiEndpoints.circuits.originalImages(circuitId),
    {
      params: { accessToken },
    },
  );
  return result.data;
};

export const listDistributorEditableCircuits = async (params: {
  page: number;
  pageSize: number;
  sortField: string;
  sortOrder: 'asc' | 'desc';
  filters: Record<string, string>;
}) => {
  const filtersStr = Object.entries(params.filters)
    .map(([key, value]) => `${key}:${encodeURIComponent(value)}`)
    .join(',');
  const result = await axios.get<{
    circuits: EditableCircuit[];
    total: number;
  }>(apiEndpoints.circuits.editableCircuits, {
    params: {
      page: params.page,
      pageSize: params.pageSize,
      sortField: params.sortField,
      sortOrder: params.sortOrder,
      filters: filtersStr,
    },
  });
  return result.data;
};

export const createDistributorEditableCircuit = async (circuitId: string) => {
  const result = await axios.post<{ id: string }>(
    apiEndpoints.circuits.editableCircuits,
    {
      circuitId,
    },
  );
  return result.data;
};

export const getDistributorEditableCircuit = async (
  editableCircuitId: string,
) => {
  const result = await axios.get<CircuitResponse>(
    apiEndpoints.circuits.editableCircuitDetail(editableCircuitId),
  );
  return result.data;
};

export type UpdateDistributorCircuitDto = {
  summary?: string;
  importantPoints?: string;
  priceIncludes?: string;
  priceExcludes?: string;
  modificationsConditions?: string;
  cancellationConditions?: string;
  daysTitles?: string[];
  daysDescriptions?: string[];
  nbParticipants?: number;
  nbChildren?: number;
  title?: string;
};

export const updateDistributorCircuit = async (
  editableCircuitId: string,
  dto: UpdateDistributorCircuitDto,
) => {
  const result = await axios.put<CircuitResponse>(
    apiEndpoints.circuits.editableCircuitDetail(editableCircuitId),
    dto,
  );
  return result.data;
};

export const deleteDistributorEditableCircuit = async (
  editableCircuitId: string,
) =>
  axios.delete(apiEndpoints.circuits.editableCircuitDetail(editableCircuitId));

export type ParagraphOverrideEntry = {
  distributorName: string;
  entityType: 'estimate' | 'editableCircuit';
  entityRef: string | number;
};

export type ParagraphOverrides = Record<string, ParagraphOverrideEntry[]>;

export const getCircuitParagraphOverrides = async (circuitId: string) => {
  const result = await axios.get<ParagraphOverrides>(
    apiEndpoints.circuits.paragraphOverrides(circuitId),
  );
  return result.data;
};
